import {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useWebSocket from './use-websocket';

import AuthContext from '@/contexts/auth';
import { useNotifications } from '@/contexts/notifications';
import { getTokens } from '@/lib/utils';

interface WsAnnouncementMessage {
    event?: string;
    announcement?: {
        id?: number;
        title?: string;
        type?: string;
        slug?: string;
        owner_business_name?: string;
        owner_username?: string;
    };
}

function buildWsUrl(): string | null {
    const { accessToken } = getTokens();
    if (!accessToken) return null;

    const serverUrl = import.meta.env
        .VITE_APP_SERVER_URL as string;
    if (!serverUrl) return null;

    const wsBase = serverUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '');

    return `${wsBase}/ws/notifications/?token=${accessToken}`;
}

export default function useAnnouncementNotifications() {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { state } = useContext(AuthContext);

    const handledRef = useRef<Map<string, number>>(
        new Map(),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when auth changes
    const wsUrl = useMemo(() => buildWsUrl(), [state]);

    const handleMessage = useCallback(
        (raw: unknown) => {
            const msg = raw as WsAnnouncementMessage;
            const ann = msg?.announcement;

            if (
                msg?.event !== 'announcement_created'
                || !ann
            ) {
                return;
            }

            // Deduplicate
            const dedupeKey = `announcement:${ann.id ?? ''}`;
            const now = Date.now();
            const last = handledRef.current.get(dedupeKey);
            if (last && now - last < 5000) return;
            handledRef.current.set(dedupeKey, now);

            // Clean old entries
            Array.from(handledRef.current.entries())
                .filter(([, ts]) => now - ts > 10000)
                .forEach(([key]) => {
                    handledRef.current.delete(key);
                });

            const name = ann.owner_business_name
                || ann.owner_username
                || 'Someone';

            toast.info(
                `${name} posted: ${ann.title || 'New announcement'}`,
            );

            addNotification({
                type: 'announcement_created',
                title: 'New Announcement',
                message: `${name} posted "${ann.title || 'an announcement'}"`,
            });

            queryClient.invalidateQueries({
                queryKey: ['billboard'],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications-unread-count'],
            });
        },
        [queryClient, addNotification],
    );

    return useWebSocket({
        url: wsUrl || '',
        onMessage: handleMessage,
        enabled: !!wsUrl,
    });
}
